import {AlertIcon} from '@primer/octicons-react'
import {Link} from '@primer/react'
import type {FC, RefObject} from 'react'
import {useCallback, useEffect, useMemo} from 'react'
import {announce} from '@github-ui/aria-live'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {isAllCondition, getDefaultTargetByObject, getTargetMode} from '../../helpers/conditions'
import {PLURAL_RULESET_TARGETS, TARGET_OBJECT_BY_TYPE, TARGET_OBJECT_TYPES} from '../../helpers/constants'
import {capitalize, pluralize} from '../../helpers/string'
import type {
  Condition,
  ConditionParameters,
  DetailedValidationErrors,
  IncludeExcludeParameters,
  RulesetTarget,
  TargetObjectType,
  ExpandedTargetType,
  TargetType,
  SourceType,
  RepositoryPropertyParameters,
} from '@github-ui/repos-rules-types/rules-types'
import {RefPill} from '../RefPill'
import {IncludeExcludeTarget} from './conditions/IncludeExcludeTarget'
import {PanelHeader} from './conditions/PanelHeader'
import {RepositoryTarget} from './conditions/RepositoryTarget'
import {RulesetFormErrorFlash} from '../RulesetFormErrorFlash'
import {RepositoryConditionsError} from '../rule-schema/errors/RepositoryConditionsError'
import {PushRulePublicTargetingBanner} from './PushRulePublicTargetingBanner'
import {OrganizationTarget} from './conditions/OrganizationTarget'
import type {Repository} from '@github-ui/current-repository'
import type {Enterprise, Organization} from '@github-ui/repos-types'
import {emptyParametersByType, OrganizationTargetTypeSelector} from './TargetTypeSelector'
import {RulesetSelection} from './conditions/RulesetSelection'
import type {ReposStaticProviderKey} from '@github-ui/repos-filter/providers'

import styles from './TargetsPanel.module.css'
import {clsx} from 'clsx'
import {useFeatureFlag} from '@github-ui/react-core/use-feature-flag'
import type {PickerScope} from '@github-ui/repos-picker'

const DirtyTargetsMessage: FC<{message: string; shouldAnnounce: boolean}> = ({message, shouldAnnounce}) => {
  useEffect(() => {
    if (shouldAnnounce) {
      announce(message)
    }
  }, [message, shouldAnnounce])

  return (
    <div className="text-small mt-1 d-flex color-fg-attention">
      <AlertIcon />
      <span className="ml-1" aria-live={shouldAnnounce ? 'off' : 'polite'}>
        {message}
      </span>
    </div>
  )
}

type AffectedTargetsSummaryProps = {
  rulesetPreviewCount: number | undefined
  rulesetPreviewSamples: string[] | undefined
  rulesetError: string | undefined
  rulesetPreviewUrl: string | undefined
  showOrgAdminMessage?: boolean
}

const AffectedTargetsSummary: FC<AffectedTargetsSummaryProps> = ({
  rulesetPreviewCount,
  rulesetPreviewSamples,
  rulesetError,
  rulesetPreviewUrl,
  showOrgAdminMessage,
}) => {
  if (rulesetError) {
    return <span className={clsx('text-small color-fg-muted', styles.TargetsPanelText)}>{rulesetError}</span>
  }

  if (typeof rulesetPreviewCount !== 'number') {
    return null
  }

  let count: React.ReactNode = pluralize(rulesetPreviewCount, 'target', 'targets')
  if (rulesetPreviewUrl) {
    count = (
      <Link inline target="_blank" rel="noopener noreferrer" href={rulesetPreviewUrl}>
        {count}
      </Link>
    )
  }

  return (
    <>
      <div>
        <span>
          Applies to {count}
          {rulesetPreviewCount > 0 && rulesetPreviewSamples && (
            <>{rulesetPreviewSamples.length > 1 ? ' including ' : ': '}</>
          )}
        </span>
        {rulesetPreviewCount > 0 &&
          rulesetPreviewSamples &&
          rulesetPreviewSamples.map((target: string, index: number) => (
            <span key={target}>
              {index === rulesetPreviewSamples.length - 1 && rulesetPreviewCount <= 10 && index > 0 && (
                <span className={clsx('mt-1', styles.TargetsPanelText)}>and</span>
              )}
              <RefPill param={target} />
              <span className="mt-1">
                {index < rulesetPreviewSamples.length - 1 ? ', ' : rulesetPreviewCount > 10 ? ' and others' : ''}
              </span>
            </span>
          ))}
        <span>.</span>
      </div>
      {showOrgAdminMessage && (
        <span className="text-small color-fg-muted">
          The count is based on repositories visible to an organization administrator.
        </span>
      )}
    </>
  )
}

export type TargetSource = Repository | Organization | Enterprise

export type TargetsPanelProps = {
  rulesetId?: number
  readOnly: boolean
  rulesetTarget: RulesetTarget
  rulesetPreviewCount: number | undefined
  rulesetPreviewSamples: string[] | undefined
  rulesetError: string | undefined
  rulesetPreviewUrl: string | undefined
  fnmatchHelpUrl?: string
  dirtyConditions: Condition[]
  conditions: Condition[]
  conditionErrors?: DetailedValidationErrors['conditions']
  supportedConditionTargetObjects: TargetObjectType[]
  repositoryConditionRef: RefObject<HTMLButtonElement | HTMLDivElement | null>
  refConditionRef: RefObject<HTMLButtonElement | HTMLDivElement | null>
  addOrUpdateCondition: (target: TargetType, parameters: ConditionParameters) => void
  removeCondition: (condition: Condition) => void
  source: TargetSource
  sourceType: SourceType
  additionalFilterProviders?: ReposStaticProviderKey[]
  showPropertiesBanner?: boolean
}

export const TargetsPanel: FC<TargetsPanelProps> = ({
  rulesetId,
  readOnly,
  rulesetTarget,
  rulesetPreviewCount,
  rulesetPreviewSamples,
  rulesetError,
  rulesetPreviewUrl,
  fnmatchHelpUrl,
  dirtyConditions,
  conditions,
  conditionErrors,
  supportedConditionTargetObjects,
  repositoryConditionRef,
  refConditionRef,
  addOrUpdateCondition,
  removeCondition,
  source,
  sourceType,
  additionalFilterProviders,
  showPropertiesBanner,
}) => {
  const isExisting = typeof rulesetId !== 'undefined'
  const emuTargetingEnabled = (source as Enterprise).enterpriseManaged
  /* eslint-disable-next-line @github-ui/github-monorepo/no-use-feature-flags --
     TODO: Migrate to isFeatureEnabled from @github-ui/feature-flags. See
     https://thehub.github.com/epd/engineering/dev-practicals/frontend/react/data-router/migration/app-payload-deprecation/ */
  const orgTargetSummaryEnabled = useFeatureFlag('rulesets_enterprise_org_preview')
  const announceStatusMessages = isFeatureEnabled('rulesets_target_status_message_a11y')

  const conditionsByObject = useMemo(() => {
    return TARGET_OBJECT_TYPES.reduce((map, objectType) => {
      let condition = conditions.find(c => TARGET_OBJECT_BY_TYPE[c.target] === objectType)
      if (!condition) {
        const target = getDefaultTargetByObject(objectType, {
          supportedConditionTargetObjects,
        })
        condition = {
          target: target.type,
          parameters: target.parameters,
          _dirty: true,
        }
      }
      map.set(objectType, condition)
      return map
    }, new Map<TargetObjectType, Condition>())
  }, [conditions, supportedConditionTargetObjects])

  const selectedTypeByObject = useMemo(() => {
    return TARGET_OBJECT_TYPES.reduce((map, objectType) => {
      const condition = conditionsByObject.get(objectType)!
      map.set(objectType, condition.target)
      return map
    }, new Map<TargetObjectType, TargetType>())
  }, [conditionsByObject])

  const changeConditionType = useCallback(
    (newType: ExpandedTargetType, initialParameters?: ConditionParameters) => {
      let normalizedType = newType
      if (newType === 'all_orgs') {
        normalizedType = 'organization_name'
      }
      if (newType === 'all_repos') {
        normalizedType = 'repository_name'
      }
      // Remove any existing condition for this object type
      const condition = conditions.find(
        c => TARGET_OBJECT_BY_TYPE[c.target] === TARGET_OBJECT_BY_TYPE[normalizedType as TargetType],
      )
      if (condition) {
        removeCondition(condition)
      }

      addOrUpdateCondition(normalizedType as TargetType, initialParameters || emptyParametersByType(newType))
    },
    [conditions, removeCondition, addOrUpdateCondition],
  )

  useEffect(() => {
    for (const targetObjectType of supportedConditionTargetObjects) {
      const condition = conditionsByObject.get(targetObjectType)
      if (!condition) {
        continue
      }
      const persistedCondition = conditions.find(({target}) => target === condition.target)
      if (!persistedCondition) {
        addOrUpdateCondition(condition.target, condition.parameters)
      }
    }
  }, [conditions, conditionsByObject, supportedConditionTargetObjects, addOrUpdateCondition])

  const targetsRefs = supportedConditionTargetObjects.includes('ref')
  const targetsRepos = supportedConditionTargetObjects.includes('repository')
  const targetsOrgs = supportedConditionTargetObjects.includes('organization')

  const visibilityScope = rulesetTarget === 'push' ? ['private', 'internal'] : undefined
  const pickerScope =
    sourceType === 'enterprise'
      ? ({type: 'enterprise', slug: source.ownerLogin, visibility: visibilityScope} as PickerScope)
      : ({type: 'organization', slug: source.ownerLogin, visibility: visibilityScope} as PickerScope)

  const excludedRepoConditions: TargetType[] = targetsOrgs ? ['repository_id', 'repository_name'] : []

  function targetingSubtitle(target: string, pluralTarget: string) {
    return `Which ${pluralTarget} should be matched?`
  }

  // When the `name` system property is used, targeting is locked
  // The name property is only used for dynamically created rulesets
  const repositoryCondition = conditionsByObject.get('repository')
  const lockTargeting = useMemo(() => {
    if (repositoryCondition?.target === 'repository_property') {
      const params = repositoryCondition.parameters as RepositoryPropertyParameters
      if (params.include.length === 1 && params.include[0]!.name === 'name') {
        return true
      }
    }
    return false
  }, [repositoryCondition])

  return (
    <div className={`d-flex flex-column gap-4`}>
      {targetsOrgs && (
        /* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */
        <div className={`d-flex flex-column gap-${!readOnly ? 3 : 2}`}>
          <div>
            <PanelHeader
              as="h2"
              title="Target organizations"
              subtitle={!readOnly ? targetingSubtitle('organization', 'organizations') : undefined}
            />
          </div>
          {!readOnly && !lockTargeting && (
            <div>
              <OrganizationTargetTypeSelector
                sourceType={sourceType}
                currentOrgCondition={conditionsByObject.get('organization')!}
                setOrgCondition={changeConditionType}
              />
            </div>
          )}
          <OrganizationTarget
            source={source}
            rulesetId={rulesetId}
            readOnly={readOnly || lockTargeting}
            fnmatchHelpUrl={fnmatchHelpUrl}
            rulesetTarget={rulesetTarget}
            targetType={selectedTypeByObject.get('organization')!}
            parameters={conditionsByObject.get('organization')!.parameters}
            metadata={conditionsByObject.get('organization')!.metadata}
            updateParameters={parameters => addOrUpdateCondition(selectedTypeByObject.get('organization')!, parameters)}
            supportsEmuTargeting={emuTargetingEnabled}
          />

          {isExisting &&
            orgTargetSummaryEnabled &&
            !isAllCondition(
              selectedTypeByObject.get('organization')!,
              conditionsByObject.get('organization')!.parameters,
            ) && (
              <>
                <AffectedTargetsSummary
                  rulesetPreviewCount={rulesetPreviewCount}
                  rulesetPreviewSamples={rulesetPreviewSamples}
                  rulesetError={rulesetError}
                  rulesetPreviewUrl={rulesetPreviewUrl}
                />
                <div className="d-flex flex-column">
                  {dirtyConditions.some(x => TARGET_OBJECT_BY_TYPE[x.target] === 'organization') && (
                    <DirtyTargetsMessage
                      message="Targets have changed and organization match list will update on save."
                      shouldAnnounce={announceStatusMessages}
                    />
                  )}
                </div>
              </>
            )}
        </div>
      )}
      {targetsRepos && (
        /* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */
        <div className={`d-flex flex-column gap-${!readOnly ? 3 : 2}`}>
          <div>
            <PanelHeader
              as="h2"
              title="Target repositories"
              subtitle={!readOnly ? targetingSubtitle('repository', 'repositories') : undefined}
            />
          </div>
          {conditionErrors?.repository?.[0]?.message && (
            <RepositoryConditionsError
              errors={conditionErrors?.repository}
              errorId="repo-target-error"
              errorRef={repositoryConditionRef as RefObject<HTMLDivElement | null>}
            />
          )}
          {rulesetTarget === 'push' && <PushRulePublicTargetingBanner />}
          <div data-testid="targets-repository-name-conditions">
            {!readOnly && !lockTargeting ? (
              <div>
                <RulesetSelection
                  showPropertiesBanner={showPropertiesBanner}
                  condition={conditionsByObject.get('repository')!}
                  excludedTypes={excludedRepoConditions}
                  setCondition={changeConditionType}
                  scope={pickerScope}
                  updateParameters={parameters =>
                    addOrUpdateCondition(selectedTypeByObject.get('repository')!, parameters)
                  }
                  additionalFilterProviders={additionalFilterProviders}
                />
                {getTargetMode(conditionsByObject.get('repository')!) === 'repository_name' && (
                  <div className="tmp-mt-3">
                    <IncludeExcludeTarget
                      rulesetId={rulesetId}
                      readOnly={readOnly}
                      rulesetTarget={rulesetTarget}
                      fnmatchHelpUrl={fnmatchHelpUrl}
                      parameters={conditionsByObject.get('repository')!.parameters as IncludeExcludeParameters}
                      panelTitle="Targeting criteria"
                      targetType="repository_name"
                      updateParameters={parameters =>
                        addOrUpdateCondition(selectedTypeByObject.get('repository')!, parameters)
                      }
                      blankslate={{
                        heading: 'No repository targets have been added yet',
                      }}
                    />
                  </div>
                )}
              </div>
            ) : (
              <RepositoryTarget
                rulesetId={rulesetId}
                readOnly={readOnly || lockTargeting}
                fnmatchHelpUrl={fnmatchHelpUrl}
                rulesetTarget={rulesetTarget}
                targetType={selectedTypeByObject.get('repository')!}
                parameters={conditionsByObject.get('repository')!.parameters}
                metadata={conditionsByObject.get('repository')!.metadata}
                updateParameters={parameters =>
                  addOrUpdateCondition(selectedTypeByObject.get('repository')!, parameters)
                }
              />
            )}

            {!targetsOrgs &&
              isExisting &&
              !isAllCondition(
                selectedTypeByObject.get('repository')!,
                conditionsByObject.get('repository')!.parameters,
              ) &&
              getTargetMode(conditionsByObject.get('repository')!) === 'repository_name' && (
                <>
                  <AffectedTargetsSummary
                    rulesetPreviewCount={rulesetPreviewCount}
                    rulesetPreviewSamples={rulesetPreviewSamples}
                    rulesetError={rulesetError}
                    rulesetPreviewUrl={rulesetPreviewUrl}
                    showOrgAdminMessage
                  />
                  {dirtyConditions.some(x => TARGET_OBJECT_BY_TYPE[x.target] === 'repository') && (
                    <DirtyTargetsMessage
                      message="Targets have changed and repository match list will update on save."
                      shouldAnnounce={announceStatusMessages}
                    />
                  )}
                </>
              )}
          </div>
        </div>
      )}
      {targetsRefs && (
        /* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */
        <div className={`d-flex flex-column gap-${!readOnly ? 3 : 2}`}>
          <div>
            <PanelHeader
              as="h2"
              title={`Target ${pluralize(2, rulesetTarget, PLURAL_RULESET_TARGETS, false)}`}
              subtitle={
                !readOnly
                  ? targetingSubtitle(rulesetTarget, pluralize(2, rulesetTarget, PLURAL_RULESET_TARGETS, false))
                  : undefined
              }
            />
          </div>
          {conditionErrors?.ref?.[0]?.message && (
            <RulesetFormErrorFlash
              errorId="ref-target-error"
              errorRef={refConditionRef as RefObject<HTMLDivElement | null>}
            >
              {conditionErrors.ref[0].message}
            </RulesetFormErrorFlash>
          )}
          <div data-testid="targets-ref-name-conditions">
            <IncludeExcludeTarget
              rulesetId={rulesetId}
              readOnly={readOnly}
              rulesetTarget={rulesetTarget}
              fnmatchHelpUrl={fnmatchHelpUrl}
              parameters={conditionsByObject.get('ref')!.parameters as IncludeExcludeParameters}
              panelTitle={`${capitalize(rulesetTarget)} targeting criteria`}
              targetType="ref_name"
              updateParameters={parameters => addOrUpdateCondition(selectedTypeByObject.get('ref')!, parameters)}
              blankslate={{
                heading: `${capitalize(rulesetTarget)} targeting has not been configured`,
              }}
            />

            {!targetsRepos && isExisting && (
              <>
                <AffectedTargetsSummary
                  rulesetPreviewCount={rulesetPreviewCount}
                  rulesetPreviewSamples={rulesetPreviewSamples}
                  rulesetError={rulesetError}
                  rulesetPreviewUrl={rulesetPreviewUrl}
                />
                {dirtyConditions.findIndex(x => TARGET_OBJECT_BY_TYPE[x.target] === 'ref') >= 0 && (
                  <DirtyTargetsMessage
                    message={`Targets have changed and ${rulesetTarget} match list will update on save.`}
                    shouldAnnounce={announceStatusMessages}
                  />
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
